// "Pricing exposure detected" in Supplier Pricing (ledger row 132, AC34).
//
// When one side of a product line is fixed and the other floats, the company
// carries price risk until the hedging desk covers it. The 9 Sept alert only
// emailed; this banner tells the person pricing the purchase side, while they
// price it, that saving will hand the desk a task -- and, once the task
// exists, where it stands.
//
// It reads the pricing types from the form as typed, so it appears the moment
// the toggles disagree, before any save. The task status and the preview come
// from the server (the enquiry as last saved).
//
// Never shown to a customer trader: the purchase side is not part of their
// view of the deal, on screen or in what the server sends (AC24).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import floatingFormulaService from '../../../services/floatingFormulaService';
import { productShorthand } from '../../../utils/productShorthand';
import HedgingTaskPreviewModal, { taskStatusText } from './HedgingTaskPreviewModal';
import { useFloatingFormulaContext } from './FloatingFormulaContext';
import { formProductLines } from './floatingFormulaGate';
import { quantityText, serverSentence, totalQuantityText } from './floatingFormulaText';
import './FloatingFormula.css';

const PRICING_TYPES = new Set(['fixed', 'floating']);

// Lines whose two sides are both answered and disagree -- the hedging
// alert's own rule (fixed vs floating).
export const exposedLines = (formState, lookups) => {
  const lines = formProductLines(formState);
  const several = lines.length > 1;
  return lines
    .filter(({ row }) => (
      PRICING_TYPES.has(row.pricing_type)
      && PRICING_TYPES.has(row.supplier_pricing_type)
      && row.pricing_type !== row.supplier_pricing_type
    ))
    .map(({ number, row }) => {
      const name = productShorthand(lookups, row);
      return {
        number,
        row,
        where: several ? `Product ${number}${name ? ` · ${name}` : ''}` : (name || 'this product'),
        direction: row.pricing_type === 'floating'
          ? 'purchase is fixed and sale is floating'
          : 'purchase is floating and sale is fixed',
        quantity: quantityText(row.min_tonnage, row.max_tonnage),
      };
    });
};

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const exposureSentence = (exposures) => {
  if (exposures.length === 1) {
    return `${capitalise(exposures[0].direction)} on ${exposures[0].where}.`;
  }
  const parts = exposures.map((e) => (
    `${e.direction} on ${e.where} (${e.quantity || 'quantity not entered yet'})`
  ));
  return `${capitalise(parts.join('; '))}.`;
};

const HedgingExposureBanner = ({ formState, lookups }) => {
  const ctx = useFloatingFormulaContext();
  const [task, setTask] = useState({ status: 'idle', data: null, error: null });
  const [previewOpen, setPreviewOpen] = useState(false);
  const latestRequest = useRef(0);

  const hidden = !ctx || ctx.isCustomerTrader;
  const exposures = hidden || !formState ? [] : exposedLines(formState, lookups);
  const exposed = exposures.length > 0;
  const enquiryId = hidden ? null : ctx.enquiryId;
  const formulasVersion = ctx?.version;

  const loadTask = useCallback(async () => {
    if (enquiryId == null) return;
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    setTask((prev) => ({ ...prev, status: prev.data ? prev.status : 'loading', error: null }));
    try {
      const data = await floatingFormulaService.hedgingTaskPreview(enquiryId);
      if (request !== latestRequest.current) return;
      setTask({ status: 'ready', data, error: null });
    } catch (err) {
      if (request !== latestRequest.current) return;
      setTask((prev) => ({
        ...prev,
        status: 'failed',
        error: serverSentence(err, 'The hedging desk task could not be checked.'),
      }));
    }
  }, [enquiryId]);

  // Read again whenever the formulas are (every save, every formula change):
  // those are the moments the task can have been created or changed.
  useEffect(() => {
    if (exposed) loadTask();
  }, [exposed, loadTask, formulasVersion]);

  if (hidden || !exposed) return null;

  const existingTask = task.data?.task || null;
  const total = exposures.length === 1
    ? exposures[0].quantity
    : totalQuantityText(exposures.map((e) => e.row));
  let taskSentence;
  if (existingTask) {
    taskSentence = `${taskStatusText(existingTask)}.`;
  } else if (total) {
    taskSentence = `Saving sends the hedging desk a task for ${total}.`;
  } else {
    taskSentence = 'Saving sends the hedging desk a task; the quantity is not entered yet.';
  }

  const openPreview = () => {
    setPreviewOpen(true);
    loadTask();
  };

  return (
    <div className="ff-exposure" role="status" data-testid="ff-exposure-banner">
      <FontAwesomeIcon icon={faTriangleExclamation} className="ff-exposure-icon" />
      <div className="ff-exposure-body">
        <p className="ff-exposure-text">
          <strong>Pricing exposure detected</strong> — {exposureSentence(exposures)} {taskSentence}
        </p>
        {task.status === 'failed' && (
          <p className="ff-exposure-check">
            Could not check whether the hedging desk already has a task for this
            enquiry: {task.error}
          </p>
        )}
      </div>
      <button
        type="button"
        className="fef-btn fef-btn-secondary ff-exposure-btn"
        onClick={openPreview}
        disabled={enquiryId == null}
        title={enquiryId == null ? 'Save the enquiry first.' : undefined}
      >
        Preview task
      </button>

      {previewOpen && (
        <HedgingTaskPreviewModal
          onClose={() => setPreviewOpen(false)}
          loading={task.status === 'loading'}
          error={task.status === 'failed' ? task.error : null}
          preview={task.data}
        />
      )}
    </div>
  );
};

export default HedgingExposureBanner;
